<template>
    <div class="create-quiz">
        <Loader v-if="isLoading" />
        <v-container v-else class="p-0" fluid>
            <div
                class="page-card"
                data-aos="fade-left"
                data-aos-duration="1500"
            >
                <h1 class="create-quiz__card-title mb-6 mb-md-10">
                    <span v-if="!isEditing">Novo</span>
                    <span v-else>Editar</span>
                    questionário
                </h1>
                <v-row>
                    <v-col cols="12" md="6">
                        <label
                            class="create-quiz__form-label required"
                            for="createQuizTitle"
                            >Título</label
                        >
                        <input
                            type="text"
                            id="createQuizTitle"
                            v-model="quiz.title"
                            class="create-quiz__form-input"
                            placeholder="Escolha um nome que chame a atenção"
                            required
                        />
                    </v-col>
                    <v-col cols="6" md="3">
                        <div class="ml-md-auto d-md-table">
                            <label
                                class="create-quiz__form-label"
                                for="createQuizRequiredScore"
                                >Exigir nota para aprovação</label
                            >
                            <CustomToggle
                                id="createQuizRequiredScore"
                                v-model="requiredScore"
                            />
                        </div>
                    </v-col>
                    <transition name="fade">
                        <v-col cols="6" md="3" v-show="requiredScore">
                            <div class="mx-md-auto d-md-table">
                                <label
                                    class="create-quiz__form-label"
                                    for="createQuizApprovalPercentage"
                                    >Porcentagem para aprovação</label
                                >
                                <div
                                    class="create-quiz__append-input-group approval-percentage"
                                >
                                    <input
                                        type="number"
                                        id="createQuizApprovalPercentage"
                                        v-model="approvalPercentage"
                                        class="create-quiz__form-input"
                                        :disabled="!requiredScore"
                                        min="0"
                                        max="100"
                                    />
                                    <span
                                        class="create-quiz__append-input-group-text"
                                        >%</span
                                    >
                                </div>
                            </div>
                        </v-col>
                    </transition>
                </v-row>
                <div class="mt-8 mt-md-12">
                    <h2 class="create-quiz__section-title mb-4">Questões</h2>

                    <draggable
                        v-if="questions && questions.length"
                        v-model="questions"
                        class="create-quiz__questions-list"
                        ghost-class="ghost"
                        tag="transition-group"
                        :component-data="{
                            tag: 'ul',
                            type: 'transition-group',
                            name: !drag ? 'flip-list' : null,
                        }"
                        v-bind="dragOptions"
                        @start="drag = true"
                        @end="drag = false"
                        handle=".question-card__handle-icon"
                    >
                        <template #item="{ element, index }">
                            <li
                                class="question-card"
                                :key="element.id && `questionCard${index}`"
                            >
                                <div
                                    class="question-card__header"
                                    @click="toggleQuestionCard(index)"
                                >
                                    <div class="flex align-items-center">
                                        <span
                                            class="question-card__handle-icon mr-2"
                                        >
                                            <fa
                                                icon="fa-solid fa-align-justify"
                                            />
                                        </span>
                                        <h3 class="question-card__title mb-0">
                                            Questão {{ index + 1 }}
                                        </h3>
                                        <h4
                                            class="question-card__description-preview ml-md-6"
                                            v-show="!openedQuestions[index]"
                                            v-html="
                                                formatQuestionDescriptionPreview(
                                                    element.description
                                                )
                                            "
                                        ></h4>
                                    </div>
                                    <div class="flex align-items-center">
                                        <button
                                            title="Remover questão"
                                            class="question-card__delete-icon mr-3 mr-md-5"
                                            @click.stop.prevent="
                                                openDeleteQuestionModal(index)
                                            "
                                        >
                                            <fa icon="fa-trash" />
                                        </button>
                                        <button
                                            class="question-card__chevron-icon"
                                        >
                                            <fa
                                                icon="fa-chevron-down"
                                                v-show="!openedQuestions[index]"
                                            />
                                            <fa
                                                icon="fa-chevron-up"
                                                v-show="openedQuestions[index]"
                                            />
                                        </button>
                                    </div>
                                </div>
                                <transition name="accordion">
                                    <div v-show="openedQuestions[index]">
                                        <div class="question-card__body">
                                            <div class="mb-4 mb-md-6">
                                                <h6
                                                    class="font-weight-bold mb-2"
                                                >
                                                    Enunciado
                                                </h6>
                                                <div
                                                    class="custom-quill-wrapper"
                                                >
                                                    <QuillEditor
                                                        v-model:content="
                                                            element.description
                                                        "
                                                        toolbar="essential"
                                                        contentType="html"
                                                        rows="4"
                                                        class="custom-quill-editor"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <h6
                                                    class="font-weight-bold mb-2"
                                                >
                                                    Alternativas
                                                </h6>
                                                <ul
                                                    class="question-card__answers-list"
                                                >
                                                    <li
                                                        v-for="(
                                                            answer, answerIndex
                                                        ) in element.answers"
                                                        :key="
                                                            answer.id
                                                                ? answer.id
                                                                : `answer${answerIndex}OfQuestion${index}`
                                                        "
                                                        class="question-card__answer-item mb-4 mb-md-6"
                                                    >
                                                        <input
                                                            type="radio"
                                                            title="Definir como resposta correta"
                                                            :name="
                                                                'selectedAnswer' +
                                                                index
                                                            "
                                                            class="form-check-input create-quiz__form-radio mr-2 mr-md-4"
                                                            :checked="
                                                                answer.correct
                                                            "
                                                            @change="
                                                                handleCorrectAnswer(
                                                                    index,
                                                                    answerIndex
                                                                )
                                                            "
                                                        />
                                                        <input
                                                            type="text"
                                                            v-model="
                                                                answer.description
                                                            "
                                                            class="create-quiz__form-input"
                                                            :class="
                                                                answer.correct &&
                                                                'correct-answer'
                                                            "
                                                        />

                                                        <button
                                                            title="Remover alternativa"
                                                            class="question-card__delete-icon ml-2 ml-md-5"
                                                            @click="
                                                                removeAnswer(
                                                                    index,
                                                                    answerIndex
                                                                )
                                                            "
                                                        >
                                                            <fa
                                                                icon="fa-trash"
                                                            />
                                                        </button>
                                                    </li>
                                                </ul>
                                                <button
                                                    type="button"
                                                    class="default-btn light-purple-btn mx-auto"
                                                    @click="addNewAnswer(index)"
                                                    v-show="
                                                        !maxAnswersReached(
                                                            index
                                                        )
                                                    "
                                                >
                                                    Adicionar alternativa
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </transition>
                            </li>
                        </template>
                    </draggable>

                    <button
                        class="default-btn purple-btn btn-prepend-icon mx-auto mt-5 mt-md-6"
                        @click="addNewQuestion()"
                        v-show="!maxQuestionsReached()"
                    >
                        <fa icon="fa-sharp fa-plus" />
                        <span> Adicionar questão </span>
                    </button>
                </div>
                <hr class="w-100 mx-0 mt-10 mt-md-16 mb-5" />
                <div
                    class="flex align-items-center justify-content-center justify-content-md-end"
                >
                    <button
                        class="default-btn white-btn mr-3 mr-md-4"
                        @click="openCancelModal()"
                    >
                        <span> Voltar </span>
                    </button>
                    <button 
                        class="default-btn purple-btn" 
                        @click="saveAll()"
                        :disabled="isLoading"
                    >
                        <span> Salvar </span>
                    </button>
                </div>
            </div>
        </v-container>

        <v-dialog
            v-model="deleteQuestionModal"
            class="delete-question-dialog"
            max-width="500px"
            transition="fade"
        >
            <v-card class="delete-question-dialog__card pa-3 pa-md-5">
                <v-card-title class="px-0"> Deletar questão </v-card-title>
                <div class="px-0">
                    <p>
                        Tem certeza de que deseja excluir a
                        <strong>questão {{ deleteQuestionIndex + 1 }}</strong
                        >?
                    </p>
                </div>
                <v-card-actions class="px-0 justify-content-end">
                    <v-btn color="" @click="deleteQuestionModal = false"
                        >Cancelar</v-btn
                    >
                    <v-btn
                        variant="danger"
                        color="danger"
                        @click="removeQuestion()"
                        >Sim, excluir</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="cancelModal"
            class="cancel-quiz-dialog"
            max-width="500px"
            transition="fade"
        >
            <v-card class="cancel-quiz-dialog__card pa-3 pa-md-5">
                <v-card-title class="px-0"> Sair da página </v-card-title>
                <div class="px-0">
                    <p>
                        Tem certeza de que deseja sair da página?
                        <strong
                            >Todas as alterações não salvas serão
                            perdidas.</strong
                        >
                    </p>
                </div>
                <v-card-actions class="px-0 justify-content-end">
                    <v-btn color="" @click="cancelModal = false">Não</v-btn>
                    <v-btn
                        variant="danger"
                        color="danger"
                        @click="$router.push('/dashboard/curso')"
                        >Sim, voltar</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
    import { mapState } from 'vuex';
    import draggable from 'vuedraggable';
    import { QuillEditor } from '@vueup/vue-quill';
    import Loader from '@/components/Loader/loader.vue';
    import CustomToggle from '@/components/Form/CustomToggle.vue';

    import apiQuiz from '@/utils/quiz';
    import apiAnswer from '@/utils/answer';
    import apiLesson from '@/utils/lesson';
    import { toast } from 'vue3-toastify';

    export default {
        name: 'CreateQuiz',
        components: {
            draggable,
            QuillEditor,
            Loader,
            CustomToggle,
        },
        data() {
            return {
                lessonData: null,
                quiz: {
                    title: '',
                    minimum: 0,
                    maximum: 10,
                },
                requiredScore: false,
                approvalPercentage: 70,
                questions: [
                    {
                        description: '',
                        order: 0,
                        status: 1,
                        answers: [
                            {
                                description: '',
                                correct: 1,
                            },
                        ],
                    },
                ],
                openedQuestions: [true],
                maxQuestions: 400,
                maxAnswers: 5,
                isLoading: false,
                drag: false,
                deleteQuestionIndex: null,
                deleteQuestionModal: false,
                cancelModal: false,
                hasUnsavedChanges: false,
                initialFormState: null,
                isInitialized: false,
                isReloading: false,
            };
        },
        computed: {
            ...mapState(['token', 'currentModule', 'currentCollections']),
            isEditing() {
                return !!this.$route.params.id;
            },
            lessonId() {
                return this.$route.params.id || null;
            },
            dragOptions() {
                return {
                    animation: 200,
                    group: 'description',
                    disabled: false,
                    ghostClass: 'ghost',
                };
            },
        },
        watch: {
            quiz: {
                handler() {
                    if (this.isInitialized && !this.hasUnsavedChanges) {
                        this.checkForChanges();
                    }
                },
                deep: true,
            },
            requiredScore(newVal) {
                this.quiz.minimum = newVal ? this.approvalPercentage / 10 : 0;
                if (this.isInitialized && !this.hasUnsavedChanges) {
                    this.checkForChanges();
                }
            },
            approvalPercentage(newVal, oldVal) {
                this.approvalPercentage = Math.round(newVal);
                if (newVal < 0 || newVal > 100) {
                    this.approvalPercentage = oldVal;
                }
                this.quiz.minimum = this.approvalPercentage / 10;
                if (this.isInitialized && !this.hasUnsavedChanges) {
                    this.checkForChanges();
                }
            },
            questions: {
                handler() {
                    if (this.isInitialized && !this.hasUnsavedChanges) {
                        this.checkForChanges();
                    }
                },
                deep: true,
            },
        },
        beforeMount() {
            if (this.isEditing) {
                this.getAllData();
            } else {
                this.$nextTick(() => {
                    this.isInitialized = true;
                    this.saveInitialState();
                });
            }
        },
        methods: {
            async getAllData() {
                this.isLoading = true;
                this.isInitialized = false;

                try {
                    if (this.lessonId) {
                        await this.getLessonData(this.lessonId);
                    }
                } catch (error) {
                    this.notify(
                        'Erro ao carregar os dados do questionário.',
                        'error'
                    );
                    console.error('Error fetching data:', error);
                } finally {
                    this.isLoading = false;
                    this.$nextTick(() => {
                        this.saveInitialState();
                        this.isInitialized = true;
                    });
                }
            },
            async getLessonData(lessonId) {
                try {
                    const lesson = await apiLesson.getDataLessonById(
                        lessonId,
                        this.token
                    );
                    this.lessonData = lesson;
                    await this.getQuizData(lesson.idQuiz);
                } catch (error) {
                    console.error('Error fetching lesson data:', error);
                }
            },
            async getQuizData(idQuiz) {
                if (!idQuiz) {
                    this.isLoading = false;
                    return;
                }
                try {
                    const quiz = await apiQuiz.getDataQuizById(
                        idQuiz,
                        this.token
                    );
                    this.quiz = quiz;
                    this.requiredScore = quiz.minimum > 0;
                    this.approvalPercentage = quiz.minimum * 10;
                    await this.getQuestionsByQuiz(idQuiz);
                } catch (error) {
                    console.error('Error fetching quiz data:', error);
                }
            },
            async getQuestionsByQuiz(idQuiz) {
                try {
                    const response = await apiAnswer.getQuestionsByQuiz(
                        idQuiz,
                        this.token
                    );
                    // Clear existing questions
                    this.questions.splice(0, this.questions.length);
                    // Add fetched questions
                    if (Array.isArray(response)) {
                        this.questions.push(...response);
                    }
                    // Mark the first question as opened
                    this.openedQuestions = this.questions.map(
                        (_, idx) => idx === 0
                    );
                } catch (error) {
                    console.error('Error fetching questions:', error);
                }
            },
            formatQuestionDescriptionPreview(description) {
                // Strip HTML tags and limit preview length for better UX
                if (!description) return '';
                const text = description.replace(/<[^>]+>/g, '').trim();
                return text.length > 60 ? text.slice(0, 60) + '...' : text;
            },
            toggleQuestionCard(index) {
                this.openedQuestions[index] = !this.openedQuestions[index];
            },
            handleCorrectAnswer(questionIndex, answerIndex) {
                const answers = this.questions[questionIndex].answers;
                answers.forEach((answer, idx) => {
                    answer.correct = idx === answerIndex ? 1 : 0;
                });
            },
            addNewAnswer(questionIndex) {
                if (this.maxAnswersReached(questionIndex)) return;
                const answers = this.questions[questionIndex].answers;
                answers.push({
                    description: '',
                    correct: 0,
                });
            },
            removeAnswer(questionIndex, answerIndex) {
                const answers = this.questions[questionIndex].answers;
                if (answers.length <= 1) return; // Prevent removing the last answer
                answers.splice(answerIndex, 1);
                // Ensure at least one answer is marked correct
                if (!answers.some((a) => a.correct)) {
                    answers[0].correct = 1;
                }
            },
            addNewQuestion() {
                if (this.maxQuestionsReached()) return;
                const questionsQuantity = this.questions.length;
                this.questions.push({
                    description: '',
                    order: questionsQuantity,
                    status: 1,
                    answers: [
                        {
                            description: '',
                            correct: 1, // Default first answer as correct
                        },
                    ],
                });
                // Open the newly added question
                this.openedQuestions.push(true);
            },
            async removeQuestion() {
                if (this.deleteQuestionIndex === null) return;
                try {
                    const questionPayload = {
                        ...this.questions[this.deleteQuestionIndex],
                        idQuiz: this.quiz.id,
                        status: 0,
                    };
                    if (this.questions[this.deleteQuestionIndex].id) {
                        await apiAnswer.editQuestionAnswer(
                            this.questions[this.deleteQuestionIndex].id,
                            questionPayload,
                            this.token
                        );
                    }
                    this.questions.splice(this.deleteQuestionIndex, 1);
                    this.openedQuestions.splice(this.deleteQuestionIndex, 1);
                    this.deleteQuestionModal = false;
                    this.notify('Questão removida com sucesso!', 'success');
                } catch (error) {
                    this.notify('Erro ao remover a questão.', 'error');
                    console.error(error);
                }
            },
            maxQuestionsReached() {
                return this.questions.length >= this.maxQuestions;
            },
            maxAnswersReached(questionIndex) {
                return (
                    this.questions[questionIndex].answers.length >=
                    this.maxAnswers
                );
            },
            validateQuiz() {
                if (!this.quiz.title.trim()) {
                    this.notify(
                        'O título do questionário é obrigatório.',
                        'error'
                    );
                    return false;
                }
                return true;
            },
            validateQuestions() {
                if (!this.questions.length) {
                    this.notify('Adicione pelo menos uma questão.', 'error');
                    return false;
                }
                for (const [qIdx, q] of this.questions.entries()) {
                    if (
                        !q.description ||
                        !q.description.replace(/<[^>]+>/g, '').trim()
                    ) {
                        this.notify(
                            `A questão ${qIdx + 1} precisa de um enunciado.`,
                            'error'
                        );
                        return false;
                    }
                    if (!q.answers || q.answers.length < 1) {
                        this.notify(
                            `A questão ${
                                qIdx + 1
                            } precisa de pelo menos 1 alternativa.`,
                            'error'
                        );
                        return false;
                    }
                    if (!q.answers.some((a) => a.correct)) {
                        this.notify(
                            `A questão ${
                                qIdx + 1
                            } precisa de uma alternativa correta.`,
                            'error'
                        );
                        return false;
                    }
                    for (const [aIdx, a] of q.answers.entries()) {
                        if (!a.description.trim()) {
                            this.notify(
                                `A alternativa ${aIdx + 1} da questão ${
                                    qIdx + 1
                                } está vazia.`,
                                'error'
                            );
                            return false;
                        }
                    }
                }
                return true;
            },
            async saveAll() {
                if (this.isLoading) return; // Previne múltiplas execuções simultâneas
                
                try {
                    this.isLoading = true;
                    if (!this.validateQuiz() || !this.validateQuestions()) {
                        return;
                    }

                    await this.saveQuiz();
                    await this.saveQuestions();
                    await this.saveLesson();

                    this.saveInitialState();
                    this.notify('Questionário salvo com sucesso!', 'success');
                    

                } catch (error) {
                    console.error('Error saving quiz data:', error);
                    this.notify(
                        'Erro ao salvar o questionário. Tente novamente.',
                        'error'
                    );
                } finally {
                    this.isLoading = false;
                }
            },
            async saveQuiz() {
                const quizPayload = {
                    ...this.quiz,
                    status: 1,
                };
                let response;
                if (this.quiz.id) {
                    response = await apiQuiz.editQuiz(
                        this.quiz.id,
                        quizPayload,
                        this.token
                    );
                } else {
                    response = await apiQuiz.createNewQuiz(
                        quizPayload,
                        this.token
                    );
                }
                if (response) {
                    this.quiz = response;
                }
            },
            async saveQuestions() {
                const questionsPayload = this.questions.map((q, index) => ({
                    ...q,
                    order: index + 1,
                    idQuiz: this.quiz.id,
                }));

                const questionsToEdit = questionsPayload.filter((q) => q.id);
                const questionsToCreate = questionsPayload.filter((q) => !q.id);

                let createdQuestions = [];
                let editedQuestions = [];

                if (questionsToEdit.length > 0) {
                    const response = await apiAnswer.editMultiQuestionAnswer(
                        questionsToEdit,
                        this.token
                    );
                    if (Array.isArray(response)) {
                        editedQuestions = response;
                    }
                }

                if (questionsToCreate.length > 0) {
                    const response = await apiAnswer.createMultiQuestionAnswer(
                        questionsToCreate,
                        this.token
                    );
                    if (Array.isArray(response)) {
                        createdQuestions = response;
                    }
                }

                const updatedQuestions = [
                    ...editedQuestions,
                    ...createdQuestions,
                ];
                if (updatedQuestions.length > 0) {
                    this.questions = updatedQuestions
                        .sort((a, b) => a.order - b.order)
                        .map((q) => ({
                            ...q,
                            answers: Array.isArray(q.answers) ? q.answers : [],
                        }));
                }
            },
            async saveLesson() {
                const commonPayload = {
                    title: this.quiz.title,
                    status: 1,
                    idQuiz: this.quiz.id,
                };

                const lessonPayload = this.isEditing
                    ? {
                          ...this.lessonData,
                          ...commonPayload,
                          idLesson: this.lessonId,
                      }
                    : {
                          ...commonPayload,
                          description: this.quiz.description || '',
                          type: 5,
                          idModule: this.currentModule?.id,
                          idCourse: this.currentCollections?.id,
                      };

                let response;
                if (this.lessonId) {
                    response = await apiLesson.editLesson(
                        this.token,
                        lessonPayload,
                        this.lessonId
                    );
                } else {
                    response = await apiLesson.createNewLesson(
                        lessonPayload,
                        this.token
                    );
                }

                if (response) {
                    this.lessonData = response;
                }
            },
            notify(message, type = 'info') {
                toast(message, {
                    autoClose: 3000,
                    position: 'top-right',
                    theme: 'light',
                    limit: 1,
                    type: type,
                    dangerouslyHTMLString: true,
                    persist: true,
                });
            },
            openDeleteQuestionModal(index) {
                this.deleteQuestionIndex = index;
                this.deleteQuestionModal = true;
            },
            saveInitialState() {
                this.initialFormState = {
                    quiz: JSON.parse(JSON.stringify(this.quiz)),
                    requiredScore: this.requiredScore,
                    approvalPercentage: this.approvalPercentage,
                    questions: JSON.parse(JSON.stringify(this.questions)),
                };
                this.hasUnsavedChanges = false;
            },

            checkForChanges() {
                if (!this.initialFormState || !this.isInitialized) return;

                const currentState = {
                    quiz: JSON.parse(JSON.stringify(this.quiz)),
                    requiredScore: this.requiredScore,
                    approvalPercentage: this.approvalPercentage,
                    questions: JSON.parse(JSON.stringify(this.questions)),
                };

                this.hasUnsavedChanges =
                    JSON.stringify(currentState) !==
                    JSON.stringify(this.initialFormState);
            },

            openCancelModal() {
                if (this.hasUnsavedChanges) {
                    this.cancelModal = true;
                } else {
                    this.$router.push('/dashboard/curso');
                }
            },
        },
    };
</script>

<style lang="css" scoped>
    ul {
        list-style: none;
        padding: 0;
    }

    svg {
        width: auto;
        max-width: 100%;
    }

    .create-quiz {
        text-align: start;
        color: #3a3a3a;
    }

    .create-quiz__card {
        width: 100%;
        padding: 16px;
        background-color: #ffffff;
        border-radius: 10px;
    }

    .create-quiz__card-title {
        color: #080919;
        font-size: 1.25rem;
        font-weight: 700;
    }

    .create-quiz__form-label {
        display: block;
        margin-bottom: 8px;
        font-size: 0.875rem;
        font-weight: 600;
    }

    .required.create-quiz__form-label::after {
        content: '*';
        color: #e10000;
    }

    .create-quiz__form-input {
        width: 100%;
        padding: 8px 12px;
        border: 1px solid #d6d8de;
        border-radius: 4px;
        font-size: 0.875rem;
        color: #080919;
    }

    .create-quiz__form-input:focus {
        border-color: #3a3a3a;
        outline: none;
    }

    .create-quiz__append-input-group {
        display: flex;
        align-items: center;
    }

    .create-quiz__append-input-group.approval-percentage {
        max-width: 120px;
    }

    .create-quiz__append-input-group .create-quiz__form-input {
        flex: 1;
        border-radius: 4px 0 0 4px;
        border-right: none;
    }

    .create-quiz__append-input-group-text {
        height: 100%;
        padding: 8px 12px;
        background-color: #f5f5f5;
        border: 1px solid #d6d8de;
        border-radius: 0 4px 4px 0;
        font-size: 0.875rem;
    }

    .create-quiz__custom-switch .v-switch__track {
        height: 1.5rem;
    }

    .create-quiz__section-title {
        color: #080919;
        font-size: 1.125rem;
        font-weight: 700;
    }

    .create-quiz__questions-list .question-card:not(:last-child) {
        margin-bottom: 16px;
    }

    .question-card {
        width: 100%;
        padding: 12px 16px;
        background-color: #ffffff;
        border: 1px solid #d6d8de;
        border-radius: 10px;
        box-shadow: 0 2px 16px 4px rgba(85, 96, 139, 0.09);
    }

    .question-card__header {
        padding: 4px 0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
    }

    .question-card__title {
        font-size: 1rem;
        font-weight: 700;
    }

    .question-card__description-preview {
        font-size: 0.875rem;
        color: #7d7d7d;
        margin-bottom: 0;
        display: none;
    }

    .question-card__description-preview * {
        margin: 0;
        padding: 0;
    }

    .question-card__handle-icon {
        cursor: move;
    }

    .question-card__handle-icon svg {
        color: #3a3a3a;
    }

    .question-card__handle-icon,
    .question-card__delete-icon,
    .question-card__chevron-icon {
        padding: 4px;
        border-radius: 50%;
        aspect-ratio: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 300ms ease;
    }

    .question-card__delete-icon .fa-trash {
        color: #e46262;
    }

    .question-card__delete-icon:hover {
        background-color: rgba(228, 98, 98, 0.15);
    }

    .question-card__header:hover .question-card__chevron-icon {
        background-color: rgba(0, 0, 0, 0.05);
    }

    .question-card__body {
        padding: 8px 0;
    }

    .question-card__answer-item {
        display: flex;
        align-items: center;
    }

    .create-quiz__form-radio {
        margin-top: 0.25em;
        vertical-align: top;
        background-color: #fff;
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
        border: 1px solid rgba(0, 0, 0, 0.25);
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        -webkit-print-color-adjust: exact;
        color-adjust: exact;
    }

    .create-quiz__form-radio[type='radio'] {
        border-radius: 50%;
    }

    .create-quiz__form-radio:checked {
        background-color: #1caa05 !important;
        border-color: #1caa05 !important;
    }

    .form-check-input.create-quiz__form-radio {
        width: 1rem !important;
        height: 1rem !important;
    }

    .form-check-input.create-quiz__form-radio.mr-2 {
        margin-right: 8px !important;
    }

    .form-check-input.create-quiz__form-radio:focus {
        box-shadow: 0 0 0 0.25rem rgba(28, 170, 5, 0.25);
    }

    .create-quiz__form-input.correct-answer {
        border: 2px solid #1caa05;
    }

    .default-btn {
        display: block;
        padding: 12px 24px;
        border: 1px solid transparent;
        border-radius: 4px;
        font-size: 0.9375rem;
        font-weight: 600;
        transition: all 0.3s ease-out;
    }

    .light-purple-btn {
        color: #0c087d;
        background-color: #e3e7fb;
    }

    .light-purple-btn:hover {
        color: #e3e7fb;
        background-color: #0c087d;
    }

    .purple-btn {
        font-weight: 700;
        background-color: #1f1e61;
        color: #e3e7fb;
    }

    .purple-btn:hover {
        color: #1f1e61;
        background-color: #e3e7fb;
        border-color: #1f1e61;
    }

    .white-btn {
        background-color: #ffffff;
        color: #1f1e61;
    }

    .white-btn:hover {
        background-color: #e3e7fb;
    }

    .btn-prepend-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 12px 24px 12px 16px;
    }

    .btn-prepend-icon span {
        display: inline-block;
    }

    .btn-prepend-icon svg {
        color: currentColor;
        height: 1.25rem;
        width: 1.25rem;
        margin-right: 12px;
    }

    .cancel-quiz-dialog .v-overlay__content,
    .edit-warning-dialog .v-overlay__content {
        width: auto !important;
        max-width: calc(100% - 48px) !important;
    }

    .cancel-quiz-dialog .modal-header,
    .edit-warning-dialog .modal-header {
        padding: 16px 24px 10px;
    }

    .cancel-quiz-dialog__card,
    .delete-question-dialog__card {
        min-width: clamp(300px, 50vw, 600px);
    }

    .v-card-actions button {
        background-color: transparent;
        width: auto;
        padding: 8px 20px;
    }

    @media screen and (min-width: 768px) {
        .create-quiz__card {
            padding: 24px;
        }

        .create-quiz__card-title {
            font-size: 1.625rem;
        }

        .create-quiz__form-input,
        .create-quiz__append-input-group-text {
            padding: 12px 16px;
        }

        .create-quiz__append-input-group .create-quiz__form-input {
            padding: 12px;
        }

        .create-quiz__section-title {
            font-size: 1.25rem;
        }

        .question-card__header {
            padding: 10px 24px;
        }

        .question-card__body {
            padding: 24px;
        }

        .question-card__description-preview {
            display: block;
        }

        .form-check-input.create-quiz__form-radio.mr-md-4 {
            margin-right: 16px !important;
        }

        .question-card__handle-icon,
        .question-card__delete-icon,
        .question-card__chevron-icon {
            padding: 8px;
        }
    }

    /* Dark Mode */
    .dark .create-quiz {
        color: #ffffff;
    }

    .dark .create-quiz__card {
        background-color: #1a1a1a;
        color: #ffffff;
    }

    .dark .create-quiz__card-title {
        color: #ffffff;
    }

    .dark .create-quiz__form-input {
        background-color: #2c2c2c;
        color: #ffffff;
        border: 1px solid #444444;
    }

    .dark .create-quiz__form-input:focus {
        border-color: #ffffff;
    }

    .dark .create-quiz__append-input-group-text {
        background-color: #2c2c2c;
        color: #ffffff;
        border: 1px solid #444444;
    }

    .dark .create-quiz__section-title {
        color: #ffffff;
    }

    .dark .create-quiz__questions-list .question-card {
        background-color: #2c2c2c;
        border: 1px solid #444444;
    }

    .dark .question-card__handle-icon svg {
        color: #ffffff;
    }

    .dark .question-card__header:hover .question-card__chevron-icon {
        background-color: rgba(255, 255, 255, 0.1);
    }

    .dark .create-quiz__form-input.correct-answer {
        border: 2px solid #1caa05;
    }

    .dark .btn-prepend-icon svg {
        color: currentColor;
    }

    .dark .purple-btn {
        background-color: #3a3a3a;
        color: #e3e7fb;
    }
    .dark .purple-btn:hover {
        color: #3a3a3a;
        background-color: #e3e7fb;
        border-color: #3a3a3a;
    }

    .dark .white-btn {
        background-color: #ffffff;
        color: #3a3a3a;
    }

    .dark .white-btn:hover {
        background-color: #e3e7fb;
    }

    .dark .cancel-quiz-dialog__card,
    .dark .delete-question-dialog__card {
        background-color: #1a1a1a;
        color: #ffffff;
    }

    /* Animations */
    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.5s ease;
    }

    .fade-enter-from,
    .fade-leave-to {
        opacity: 0;
    }

    .accordion-enter-active,
    .accordion-leave-active {
        transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s;
        overflow: hidden;
    }
    .accordion-enter-from,
    .accordion-leave-to {
        max-height: 0;
        opacity: 0;
    }
    .accordion-enter-to,
    .accordion-leave-from {
        max-height: 700px;
        opacity: 1;
    }
</style>
